import intervalTree, {
  Interval,
  IntervalTree,
} from 'interval-tree-1d';
import {
  getSVGPoint,
  ITransformationMatrix,
} from '../../network/panZoom';
import {
  NodeInternal,
} from './nodeFillShadersUtils';

// The elements are the ID, x/y center, radius and the position of this node within the list of all nodes:
export type IntervalElement = [string, number, number, number];

export const getIntervalTrees = (
    nextKeys: string[],
    nextNodes: Map<string, NodeInternal>,
  ) => {
  const nextKeysLength = nextKeys.length;
  const xIntervals: Array<Interval<IntervalElement>> = [];
  const yIntervals: Array<Interval<IntervalElement>> = [];
  for (let i = 0; i < nextKeysLength; i += 1) {
    const node = nextNodes.get(nextKeys[i])!;
    const id = node.id;
    const centerX = node.centerX;
    const centerY = node.centerY;
    const radius = node.radius;
    const x0 = centerX - radius;
    const x1 = centerX + radius;
    const y0 = centerY - radius;
    const y1 = centerY + radius;
    const xInterval: Interval<IntervalElement> = [x0, x1, [id, centerX, radius, i]];
    const yInterval: Interval<IntervalElement> = [y0, y1, [id, centerY, radius, i]];
    xIntervals.push(xInterval);
    yIntervals.push(yInterval);
  }

  return {
    xIntervalTree: intervalTree(xIntervals),
    yIntervalTree: intervalTree(yIntervals),
  };
};

export const searchForHits = (
  xIntervalTree: IntervalTree<IntervalElement>,
  yIntervalTree: IntervalTree<IntervalElement>,
  xMax: number,
  yMax: number,
  xTarget: number,
  yTarget: number,
  transformationMatrix: ITransformationMatrix,
) => {

  if (xTarget < 0 || xTarget > xMax || yTarget < 0 || yTarget > yMax) {
    return undefined;
  }  else {
    // Undo the transformation to figure out where th clicked point would have been
    // before any pan/zoom action...
    const untransformed = getSVGPoint(transformationMatrix, {x: xTarget, y: yTarget});
    const untransformedX = untransformed.x;
    const untransformedY = untransformed.y;

    // ... then try to find which circles, when projected onto the x axis, overlaps with
    // the x coord of the mouse event:
    const xMatchIds: IntervalElement[] = [];
    xIntervalTree.queryPoint(untransformedX, (interval: Interval<IntervalElement>) => {
      xMatchIds.push(interval[2]);
      return undefined;
    });
    // ... same for y direction:
    const yMatchIds: IntervalElement[] = [];
    yIntervalTree.queryPoint(untransformedY, (interval: Interval<IntervalElement>) => {
      yMatchIds.push(interval[2]);
      return undefined;
    });

    const numXMatches = xMatchIds.length;
    const numYMatches = yMatchIds.length;
    // The array consists of: the node ID, the radius and the position of this
    // node within the list of all nodes:
    type MatchElement = [string, number, number];
    const matches: MatchElement[] = [];

    // Then find intersection of `xMatchIds` and `yMatchIds`. If the
    // intersection is empty, no circle was hit. If the intersection is
    // non-empty, we need to test if the mouse event's position is within the
    // circle's boundary:
    for (let i = 0; i < numXMatches; i += 1) {
      const xInfo = xMatchIds[i];
      const xId = xInfo[0];
      for (let j = 0; j < numYMatches; j += 1) {
        const yInfo = yMatchIds[j];
        const yId = yInfo[0];
        if (xId === yId) {
          // Check if it's the point is actually inside the circle:
          const centerX = xInfo[1];
          const centerY = yInfo[1];
          const radius = xInfo[2];
          const order = xInfo[3];
          const distanceToCenter = Math.sqrt(
            (untransformedX - centerX) ** 2 + (untransformedY - centerY) ** 2,
          );
          if (distanceToCenter < radius) {
            matches.push([xId, radius, order]);
          }
        }
      }
    }

    if (matches.length === 0) {
      return undefined;
    } else {
      // If the mouse's position is in the intersection of serveral nodes, we
      // need to pick one as follows: Pick the smallest node because smaller
      // nodes are rendered above larger ones so that's the one that the user
      // meant to hover over. If there are several smallest nodes, pick the one
      // that is rendered later because it appears above those rendered before:
      matches.sort((a, b) => {
        const aRadius = a[1];
        const bRadius = b[1];
        if (aRadius === bRadius) {
          const aOrder = a[2];
          const bOrder = b[2];
          return (aOrder < bOrder) ? 1 : -1;
        } else {
          return (aRadius < bRadius) ? -1 : 1;
        }
      });
      return matches[0][0];
    }

  }
};
